import { Button, DrawerBodyWrapper, DrawerFooterWrapper, Input, Select, TOAST_TYPE } from '@base'
import { CountryCodes, DRAWER_TYPES, INSPECTION_USER_DESIGNATIONS, strings } from '@constants'
import { addOrderSiteManager, AddSiteManagerInEnquiry, getOrderById, getUserByID, getUsersOfOrganisation, updateReceiverDetails } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { Spacings } from '@styles'
import { getCountryDialCode, getNumberWithCountryCode, mobileLength } from '@utils'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

export const InputWrapper = styled.div`
	margin: ${({ margin }) => margin || 0};
	max-width: ${({ width }) => (width ? `${width}rem` : 'initial')};
	flex-grow: 1;
`

const initialFormState = {
	full_name: {
		id: 'full_name',
		label: 'Name',
		placeholder: 'Enter full name',
		value: '',
		dirty: false,
		required: true,
		valid: null,
	},
	mobile_number: {
		id: 'mobile_number',
		label: 'Mobile',
		placeholder: 'Enter mobile number',
		value: '',
		dirty: false,
		required: true,
		valid: null,
	},
	email_id: {
		id: 'email_id',
		label: 'Email',
		placeholder: 'Enter email',
		value: '',
		dirty: false,
		required: false,
	},
	designation: {
		id: 'designation',
		label: 'Designation',
		placeholder: 'Enter designation',
		value: '',
		dirty: false,
		required: false,
	},
}

const deriveFormStateFromItemData = (oldState = initialFormState, data) => {
	const derivedState = { ...oldState }

	derivedState.full_name = {
		...derivedState.full_name,
		value: data?.full_name || '',
		dirty: false,
	}
	derivedState.mobile_number = {
		...derivedState.mobile_number,
		value: data?.mobile_number?.slice(2) || '',
		dirty: false,
		valid: null,
	}
	derivedState.email_id = {
		...derivedState.email_id,
		value: data?.email_id || '',
		dirty: false,
	}
	derivedState.designation = {
		...derivedState.designation,
		value: data?.designation || '',
		dirty: false,
	}

	return derivedState
}

const defaultCountryCode = CountryCodes.find((_c) => _c.default)
const designationTypes = Object.entries(INSPECTION_USER_DESIGNATIONS).map(([key, value]) => ({
	key,
	label: value,
}))

const SiteManagerDetails = ({
	update,
	enquiry,
	userId = null,
	organisationId = null,
	InvoiceOrderId = null,
	invoiceNumber = null,
	editable = null,
	orderSiteManger,
	order = null,
	data = null, // currently unused, kept for API compatibility
}) => {
	const dispatch = useDispatch()
	const user = useSelector((state) => state.getIn(['user', 'user']))

	const [countryCode, setCountryCode] = useState(defaultCountryCode)
	const [formState, setFormState] = useState(initialFormState)
	const [userList, setUserList] = useState([])
	const [selectedUser, setSelectedUser] = useState(null)
	const [mobileValid, setMobileValid] = useState(false)

	// Fetch user by ID (for editing existing site manager)
	useEffect(() => {
		setMobileValid(false)
		if (!userId) {
			resetHandler()
			return
		}

		getUserByID(userId)
			.then((response) => {
				const code = response?.mobile_number?.slice(0, 2)
				const result = CountryCodes.find((item) => item.dial_code.includes(code)) || getCountryDialCode(code)
				if (result) setCountryCode(result)

				setFormState((_oldState) => deriveFormStateFromItemData(_oldState, response))
			})
			.catch((error) => {
				console.log(error)
			})
	}, [userId, editable])

	// Fetch users of organisation
	useEffect(() => {
		if (!organisationId) return

		dispatch(showLoader(true))
		getUsersOfOrganisation(organisationId)
			.then((response) => {
				setUserList(response || [])
			})
			.catch((error) => {
				const error_msg = error?.response?.data?.message ?? strings('msg_failed_to_fetch')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [organisationId])

	const updateState = (key, updates) => {
		setFormState((_oldState) => ({
			..._oldState,
			[key]: {
				..._oldState[key],
				...updates,
			},
		}))
	}

	const handleDesignationChange = (value) => {
		updateState('designation', { value: value?.key ?? '' })
	}

	const handleUserSelect = (value) => {
		setFormState((_oldState) => deriveFormStateFromItemData(_oldState, value))
		setSelectedUser(value)
	}

	const submitHandler = () => {
		dispatch(showLoader(true))

		const mobileWithCode = getNumberWithCountryCode(countryCode, formState.mobile_number.value)
		const commonArgs = [formState.full_name.value, mobileWithCode, formState.email_id.value, formState.designation.value]

		let apiCall
		if (orderSiteManger) {
			apiCall = addOrderSiteManager(...commonArgs, orderSiteManger?.order_id, user?.user_id, organisationId)
		} else {
			apiCall = InvoiceOrderId
				? updateReceiverDetails(...commonArgs, invoiceNumber, user?.user_id, InvoiceOrderId, organisationId)
				: AddSiteManagerInEnquiry(...commonArgs, enquiry?.enquiry_id, user?.user_id, organisationId)
		}

		apiCall
			.then(() => {
				dispatch(
					showToast(true, strings([InvoiceOrderId ? 'update' : 'add', 'site', 'manager', 'successfully']), {
						type: TOAST_TYPE.SUCCESS,
					})
				)
				// navigate to invoice details
				if (order) {
					getOrderById(order?.order_id)
						.then((response) => {
							const invoiceData = response?.buyer_list[0]?.buyer_invoices?.find((item) => item?.invoice_number === invoiceNumber)
							dispatch(toggleDrawer(true, DRAWER_TYPES.ADMIN_EDIT_ORDER_INVOICE, { invoiceData, order: response, update }))
						})
						.catch((error) => {
							console.log('error', error)
						})
				}
			})
			.catch((error) => {
				const error_msg = error?.response?.data?.message || error?.data?.message || strings('msg_something_went_wrong')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				update()
				dispatch(showLoader(false))
			})
	}

	// Disable submit button if no user selected AND required fields missing
	const disabledHandler = useMemo(() => {
		if (selectedUser) return false
		return !formState.full_name.value || !formState.mobile_number.value || !mobileValid
	}, [formState.full_name.value, formState.mobile_number.value, mobileValid, selectedUser])

	// If any field has value -> disable select-user
	const isUserSelectDisabled = useMemo(() => {
		const { full_name, mobile_number, email_id, designation } = formState
		return !selectedUser && Boolean(full_name.value || mobile_number.value || email_id.value || designation.value)
	}, [formState.full_name.value, formState.mobile_number.value, formState.email_id.value, formState.designation.value])

	// If select-user has value -> disable all fields
	const areAllFieldsDisabled = !!selectedUser

	const resetHandler = () => {
		setFormState(initialFormState)
		setSelectedUser(null)
		setCountryCode(defaultCountryCode)
	}

	return (
		<>
			<DrawerBodyWrapper>
				<Select
					id='select-user'
					label={strings('user')}
					placeholder={strings('select', 'user')}
					data={userList ?? []}
					value={userList?.find((item) => item.user_id === selectedUser?.user_id) ?? {}}
					primaryKey='user_id'
					displayKey='full_name'
					onChange={handleUserSelect}
					disabled={isUserSelectDisabled}
				/>

				<Input
					id={formState.full_name.id}
					label={formState.full_name.label}
					placeholder={formState.full_name.placeholder}
					value={formState.full_name.value ?? ''}
					required={formState.full_name.required}
					onChange={(value) => {
						updateState('full_name', { value, dirty: true })
					}}
					disabled={areAllFieldsDisabled}
				/>

				<div style={{ display: 'flex' }}>
					<InputWrapper margin={`0 ${Spacings.SPACING_2B} 0 0`} width={4}>
						<Select
							id='login-country-code'
							data={CountryCodes}
							displayKey='dial_code'
							primaryKey='name'
							value={countryCode}
							onChange={setCountryCode}
							type='tel'
							noDropIcon
							disabled={areAllFieldsDisabled}
						/>
					</InputWrapper>
					<InputWrapper>
						<Input
							id={formState.mobile_number.id}
							type='tel'
							label={formState.mobile_number.label}
							placeholder={formState.mobile_number.placeholder}
							value={formState.mobile_number.value ?? ''}
							setValidity={setMobileValid}
							pattern='^\d{7}\d+$'
							maxLength={mobileLength(countryCode.dial_code)}
							errorMap={{
								patternMismatch: `${strings('enter_valid_num')}`,
								valueMissing: `${strings('mob_num_required')}`,
							}}
							required={formState.mobile_number.required}
							onChange={(value) => {
								updateState('mobile_number', { value, dirty: true })
							}}
							hasError={(error) => {
								setFormState((_s) => ({
									..._s,
									mobile_number: {
										..._s.mobile_number,
										valid: !error,
									},
								}))
							}}
							disabled={areAllFieldsDisabled}
						/>
					</InputWrapper>
				</div>

				<Input
					id={formState.email_id.id}
					label={formState.email_id.label}
					type='email'
					pattern='^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$'
					errorMap={{
						patternMismatch: `${strings('enter_valid_email')}`,
					}}
					placeholder={formState.email_id.placeholder}
					value={formState.email_id.value}
					onChange={(value) => {
						updateState('email_id', { value, dirty: true })
					}}
					required={formState.email_id.required}
					disabled={areAllFieldsDisabled}
				/>

				<Select
					id='select-designation'
					label={strings('designation')}
					placeholder={strings('select', 'designation')}
					data={designationTypes ?? []}
					value={designationTypes.find((item) => item.key === formState.designation.value) ?? {}}
					disabled={areAllFieldsDisabled}
					primaryKey='key'
					displayKey='label'
					required={formState.designation.required}
					onChange={handleDesignationChange}
				/>
			</DrawerBodyWrapper>

			<DrawerFooterWrapper>
				<Button margin={`0 0 0 auto`} small disabled={disabledHandler} onClick={submitHandler}>
					{editable ? strings('update') : strings('submit')}
				</Button>
				<Button small type='tertiary' variant='destructive' onClick={resetHandler} margin={`0 0 0 ${Spacings.SPACING_4B}`}>
					{strings('reset')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default SiteManagerDetails
